'use client';

import Markdown from '@/app/common/Markdown';
import { ChunkAST, FootnoteDefinition } from '@/lib/parseMarkdown';
import PrintCover from './PrintCover';
import Appendices from './Appendices';
import { makePrintSidenote } from './printSidenote';
import { extractDetailsToAppendices } from './detailsToAppendices';
import { unwrapInteractiveDirectives } from './unwrapDirectives';
import { bookPageStyle, runningHeaderStyle } from './runningHeader';
import { PrintTextPrep } from './PrintTextPrep';
import { PrintGutterAlign } from './printGutterAlign';
import { PrintTableSidenotes } from './PrintTableSidenotes';
import './print.css';

export default function PrintEssay({
  nodes,
  title,
  author,
  bookMargins = false,
}: {
  nodes: ChunkAST[];
  title?: string;
  author?: string;
  // Bound-book layout (?book=1): alternating sidenote gutters via dual note
  // copies + negative spine-side page margins — one render, CMD+P-printable
  // (print.css "Bound-book layout", runningHeader.tsx bookPageStyle).
  bookMargins?: boolean;
}) {
  const footnotes: FootnoteDefinition[] =
    nodes.length > 0 ? nodes[0].docContext.footnotes : [];
  const Sidenote = makePrintSidenote(footnotes);

  // Pull the expandable boxes out of the flow into back-matter appendices.
  const { nodes: bodyNodes, appendices } = extractDetailsToAppendices(
    unwrapInteractiveDirectives(nodes.flatMap((chunk) => chunk.nodes)),
  );

  return (
    <div className={bookMargins ? 'print-root print-book' : 'print-root'}>
      <PrintTextPrep />
      {/* Before PrintGutterAlign: effects run in mount order, and the notes
          must leave their table cells before the aligner measures the floats
          (it hides copies still trapped in cells). */}
      <PrintTableSidenotes />
      {bookMargins ? <PrintGutterAlign /> : null}
      {title ? runningHeaderStyle(title) : null}
      {bookMargins ? bookPageStyle(title ?? 'AI 2040') : null}

      {title ? <PrintCover title={title} author={author} /> : null}

      <article className="print-essay">
        <div className="markdown-content print-prose">
          <Markdown nodes={bodyNodes} FootnoteHandler={Sidenote} />
        </div>
        <Appendices appendices={appendices} footnotes={footnotes} />
      </article>
    </div>
  );
}
